import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';

@Component({
  selector: 'ngx-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true,
    },
  ],
})
export class SearchableSelectComponent implements ControlValueAccessor, Validator, OnDestroy {
  private static openInstance: SearchableSelectComponent | null = null;

  @Input() options: any[] = [];
  @Input() placeholder = 'Search and select';
  @Input() displayKey = 'name';
  @Input() valueKey: string | string[] = 'value';
  @Input() noResultsText = 'No results found';
  @Input() required = false;
  @Input() multiple = false;
  @Input() disabled = false;

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('inputWrap') inputWrap?: ElementRef<HTMLElement>;

  searchText = '';
  selectedValue: any = '';
  isOpen = false;
  isOpenAbove = false;
  panelStyle: Record<string, string | number> = {};

  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };

  constructor(private hostElement: ElementRef<HTMLElement>) { }

  get displayText(): string {
    if (this.multiple) {
      const selectedValues = Array.isArray(this.selectedValue) ? this.selectedValue : [];
      return selectedValues
        .map((value) => this.getLabelFromValue(value))
        .filter(Boolean)
        .join(', ');
    }

    return this.getLabelFromValue(this.selectedValue);
  }

  get filteredOptions(): any[] {
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      return this.options || [];
    }

    return (this.options || []).filter((option) =>
      this.getOptionLabel(option).toLowerCase().includes(term),
    );
  }

  writeValue(value: any): void {
    this.selectedValue = this.multiple ? (Array.isArray(value) ? value : []) : value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled && this.isOpen) {
      this.closeDropdown();
    }
  }

  ngOnDestroy(): void {
    if (SearchableSelectComponent.openInstance === this) {
      SearchableSelectComponent.openInstance = null;
    }
  }

  validate(_: AbstractControl): ValidationErrors | null {
    if (!this.required) {
      return null;
    }

    if (this.multiple && (!Array.isArray(this.selectedValue) || !this.selectedValue.length)) {
      return { required: true };
    }

    if (!this.multiple && (
      this.selectedValue === null ||
      this.selectedValue === undefined ||
      this.selectedValue === ''
    )) {
      return { required: true };
    }

    return null;
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }

    this.isOpen ? this.closeDropdown() : this.openDropdown();
  }

  onSearchInput(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value || '';
    this.registerAsOpenInstance();
    this.isOpen = true;
    this.updatePanelPosition();
  }

  selectOption(option: any, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const optionValue = this.getOptionValue(option);

    if (this.multiple) {
      const selectedValues = Array.isArray(this.selectedValue) ? [...this.selectedValue] : [];
      const selectedIndex = selectedValues.findIndex((value) => this.isSameValue(value, optionValue));

      selectedIndex >= 0 ? selectedValues.splice(selectedIndex, 1) : selectedValues.push(optionValue);
      this.selectedValue = selectedValues;
      this.onChange(selectedValues);
      this.onTouched();
      return;
    }

    this.selectedValue = optionValue;
    this.onChange(optionValue);
    this.onTouched();
    this.closeDropdown();
  }

  isOptionSelected(option: any): boolean {
    const optionValue = this.getOptionValue(option);
    if (this.multiple) {
      return Array.isArray(this.selectedValue)
        && this.selectedValue.some((value) => this.isSameValue(value, optionValue));
    }

    return this.isSameValue(this.selectedValue, optionValue);
  }

  getOptionLabel(option: any): string {
    if (option == null) {
      return '';
    }

    if (typeof option === 'string' || typeof option === 'number') {
      return String(option);
    }

    const label = option?.[this.displayKey];
    return label == null ? '' : String(label);
  }

  private getOptionValue(option: any): any {
    if (option == null) {
      return '';
    }

    if (typeof option === 'string' || typeof option === 'number') {
      return option;
    }

    if (Array.isArray(this.valueKey)) {
      return this.valueKey.reduce((value, key) => {
        value[key] = option?.[key];
        return value;
      }, {} as Record<string, any>);
    }

    return option?.[this.valueKey];
  }

  private getLabelFromValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const matchedOption = (this.options || []).find((option) =>
      this.isSameValue(this.getOptionValue(option), value),
    );
    return matchedOption ? this.getOptionLabel(matchedOption) : '';
  }

  private isSameValue(optionValue: any, selectedValue: any): boolean {
    if (optionValue === selectedValue) {
      return true;
    }

    if (
      optionValue == null ||
      selectedValue == null ||
      typeof optionValue !== 'object' ||
      typeof selectedValue !== 'object'
    ) {
      return `${optionValue}` === `${selectedValue}`;
    }

    const optionKeys = Object.keys(optionValue);
    const selectedKeys = Object.keys(selectedValue);
    return optionKeys.length === selectedKeys.length
      && optionKeys.every((key) => optionValue[key] === selectedValue[key]);
  }

  private openDropdown(): void {
    this.registerAsOpenInstance();
    this.isOpen = true;
    this.updatePanelPosition();

    setTimeout(() => {
      if (this.isOpen) {
        this.updatePanelPosition();
        this.searchInput?.nativeElement.focus();
      }
    });
  }

  private closeDropdown(): void {
    this.isOpen = false;
    this.isOpenAbove = false;
    this.searchText = '';
    if (SearchableSelectComponent.openInstance === this) {
      SearchableSelectComponent.openInstance = null;
    }
    this.onTouched();
  }

  private registerAsOpenInstance(): void {
    const openInstance = SearchableSelectComponent.openInstance;
    if (openInstance && openInstance !== this) {
      openInstance.closeDropdown();
    }
    SearchableSelectComponent.openInstance = this;
  }

  private updatePanelPosition(): void {
    const element = this.inputWrap?.nativeElement;
    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const preferredHeight = 320;
    const minimumHeight = 160;
    const viewportPadding = 12;
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openAbove = spaceBelow < minimumHeight && spaceAbove > spaceBelow;
    const availableSpace = openAbove ? spaceAbove : spaceBelow;

    this.isOpenAbove = openAbove;
    this.panelStyle = {
      position: 'fixed',
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      maxHeight: `${Math.min(preferredHeight, Math.max(availableSpace - gap - viewportPadding, minimumHeight))}px`,
      zIndex: 99999,
      top: openAbove ? 'auto' : `${rect.bottom + gap}px`,
      bottom: openAbove ? `${Math.max(viewportPadding, window.innerHeight - rect.top + gap)}px` : 'auto',
    };
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: MouseEvent): void {
    if (this.isOpen && !this.hostElement.nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    if (this.isOpen) {
      this.updatePanelPosition();
    }
  }
}
